import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../services/api';

interface ApiConfig {
    id: string;
    service_name: string;
    provider: string;
    api_key: string;
    base_url: string;
    is_active: boolean;
}

const ApiControl: React.FC = () => {
    const [configs, setConfigs] = useState<ApiConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<ApiConfig>>({
        service_name: 'hotel',
        provider: '',
        api_key: '',
        base_url: '',
        is_active: true
    });

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            const data = await apiFetch('/admin/api-config');
            setConfigs(data || []);
        } catch (error) {
            console.error('Error fetching configs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (formData.id) {
                await apiFetch(`/admin/api-config/${formData.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(formData)
                });
            } else {
                await apiFetch('/admin/api-config', {
                    method: 'POST',
                    body: JSON.stringify(formData)
                });
            }

            setFormData({
                service_name: 'hotel',
                provider: '',
                api_key: '',
                base_url: '',
                is_active: true
            });
            setIsEditing(false);
            fetchConfigs();
        } catch (error) {
            console.error('Error saving config:', error);
            alert('Error saving configuration');
        }
    };

    const handleEdit = (config: ApiConfig) => {
        setFormData(config);
        setIsEditing(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this configuration?')) return;

        try {
            await apiFetch(`/admin/api-config/${id}`, {
                method: 'DELETE'
            });
            fetchConfigs();
        } catch (error) {
            console.error('Error deleting config:', error);
            alert('Error deleting configuration');
        }
    };

    if (loading) return <div className="text-white">Loading...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">API Configurations</h2>
                <button
                    onClick={() => {
                        setFormData({
                            service_name: 'hotel',
                            provider: '',
                            api_key: '',
                            base_url: '',
                            is_active: true
                        });
                        setIsEditing(!isEditing);
                    }}
                    className="px-4 py-2 bg-logo-gold-400 text-black rounded-lg hover:bg-logo-gold-500 transition-colors font-medium"
                >
                    {isEditing ? 'Cancel' : 'Add New Configuration'}
                </button>
            </div>

            {isEditing && (
                <div className="bg-gray-900 shadow-lg rounded-xl p-6 border border-gray-800">
                    <h3 className="text-lg font-medium text-white mb-4">
                        {formData.id ? 'Edit Configuration' : 'New Configuration'}
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-400">Service Name</label>
                                <select
                                    required
                                    className="mt-1 block w-full py-2 px-3 border border-gray-700 bg-gray-800 text-white rounded-md shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                    value={formData.service_name}
                                    onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                                >
                                    <option value="hotel">Hotel</option>
                                    <option value="flight">Flight</option>
                                    <option value="taxi">Taxi</option>
                                    <option value="payment">Payment</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400">Provider</label>
                                {formData.service_name === 'payment' ? (
                                    <select
                                        required
                                        className="mt-1 block w-full py-2 px-3 border border-gray-700 bg-gray-800 text-white rounded-md shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                        value={formData.provider}
                                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                    >
                                        <option value="">Select Provider</option>
                                        <option value="Flutterwave">Flutterwave</option>
                                        <option value="Monnify">Monnify</option>
                                        <option value="Moniepoint">Moniepoint</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        required
                                        className="mt-1 block w-full py-2 px-3 border border-gray-700 bg-gray-800 text-white rounded-md shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                        value={formData.provider}
                                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                        placeholder="e.g. Amadeus, Uber"
                                    />
                                )}
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-400">API Key</label>
                                <input
                                    type="password"
                                    className="mt-1 block w-full py-2 px-3 border border-gray-700 bg-gray-800 text-white rounded-md shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                    value={formData.api_key}
                                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-400">Base URL</label>
                                <input
                                    type="url"
                                    className="mt-1 block w-full py-2 px-3 border border-gray-700 bg-gray-800 text-white rounded-md shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                    value={formData.base_url}
                                    onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                                    placeholder="e.g. https://letsfg.co/developers/api/v1 (or /v1/sandbox for Test API)"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    For LetSFG Test API, use <span className="text-gray-300 font-mono">https://letsfg.co/developers/api/v1/sandbox</span>. For Real API, use <span className="text-gray-300 font-mono">https://letsfg.co/developers/api/v1</span>.
                                </p>
                            </div>

                            <div className="flex items-center">
                                <input
                                    id="is_active"
                                    type="checkbox"
                                    className="h-4 w-4 text-logo-gold-400 focus:ring-logo-gold-400 border-gray-700 rounded bg-gray-800"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-400">
                                    Active
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-black bg-logo-gold-400 hover:bg-logo-gold-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-logo-gold-400"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-gray-900 shadow-lg rounded-xl overflow-hidden border border-gray-800">
                <table className="min-w-full divide-y divide-gray-800">
                    <thead className="bg-gray-800">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Service
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Provider
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-gray-900 divide-y divide-gray-800">
                        {configs.map((config) => (
                            <tr key={config.id} className="hover:bg-gray-800/50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-800 text-gray-300 border border-gray-700">
                                        {config.service_name}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                    {config.provider}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.is_active
                                            ? 'bg-green-900/30 text-green-400'
                                            : 'bg-red-900/30 text-red-400'
                                        }`}>
                                        {config.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                                    <button
                                        onClick={() => handleEdit(config)}
                                        className="text-logo-gold-400 hover:text-logo-gold-300 transition-colors"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(config.id)}
                                        className="text-red-400 hover:text-red-300 transition-colors"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {configs.length === 0 && !loading && (
                            <tr>
                                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                    No API configurations found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ApiControl;
